import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  FaEdit,
  FaTools,
  FaInfoCircle,
  FaPlus,
  FaArrowLeft,
  FaFileDownload,
  FaTable,
  FaChartPie,
  FaTachometerAlt,
  FaSearch,
  FaFilter,
  FaSyncAlt,
} from "react-icons/fa";

import * as XLSX from "xlsx";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import "./BreakdownTracker1.css";

const BreakdownTracker = () => {
  const [breakdowns, setBreakdowns] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("IN_TRANSIT");
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [activeTab, setActiveTab] = useState("table");
  const gridRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFilter(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchBreakdownRecords();
  }, []);

  const fetchBreakdownRecords = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/breakdownRecords`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const normalize = (rec) => {
        const out = {};
        Object.keys(rec || {}).forEach((k) => {
          out[k.toLowerCase()] = rec[k];
        });
        return out;
      };

      setBreakdowns((response.data || []).map(normalize));
    } catch (error) {
      console.error("Error fetching breakdown records:", error);
      setBreakdowns([]);
    }
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const filteredBreakdowns = useMemo(() => {
    return breakdowns
      .filter((breakdown) =>
        selectedStatus ? breakdown.jp_status === selectedStatus : true
      )
      .filter((breakdown) =>
        globalFilter
          ? Object.values(breakdown)
              .join(" ")
              .toLowerCase()
              .includes(globalFilter.toLowerCase())
          : true
      );
  }, [breakdowns, selectedStatus, globalFilter]);

  // Chart data processing (use lowercase keys)
  const chartData = useMemo(() => {
    if (!filteredBreakdowns.length) return { pieData: [], barData: [] };

    const issueCount = filteredBreakdowns.reduce((acc, record) => {
      const issue = record.issue || "Unknown";
      acc[issue] = (acc[issue] || 0) + 1;
      return acc;
    }, {});

    const pieData = Object.keys(issueCount).map((issue) => ({
      name: issue,
      value: issueCount[issue],
      percentage: (
        (issueCount[issue] / filteredBreakdowns.length) *
        100
      ).toFixed(1),
    }));

    const carrierCount = filteredBreakdowns.reduce((acc, record) => {
      const carrier = record.carrier || "Unknown";
      acc[carrier] = (acc[carrier] || 0) + 1;
      return acc;
    }, {});

    const barData = Object.keys(carrierCount).map((carrier) => ({
      name: carrier,
      count: carrierCount[carrier],
    }));

    return { pieData, barData };
  }, [filteredBreakdowns]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return moment(date).format("DD/MM/YYYY HH:mm");
  };

  const dateFilterParams = useMemo(
    () => ({
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
        const cellDate = new Date(cellValue);
        if (Number.isNaN(cellDate.getTime())) return -1;

        const filterDate = new Date(filterLocalDateAtMidnight);
        const cellOnlyDate = new Date(
          cellDate.getFullYear(),
          cellDate.getMonth(),
          cellDate.getDate()
        );

        if (cellOnlyDate < filterDate) return -1;
        if (cellOnlyDate > filterDate) return 1;
        return 0;
      },
    }),
    []
  );

  const columnDefs = React.useMemo(
    () => [
      {
        headerName: "JP Number",
        field: "journey_plane_no",
        minWidth: 130,
      },
      {
        headerName: "Truck Number",
        field: "scheduled_vehicle",
        minWidth: 140,
      },
      {
        headerName: "Breakdown Date",
        field: "breakdown_time",
        minWidth: 170,
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        valueFormatter: ({ value }) => formatDateTime(value),
        sort: "desc",
      },
      {
        headerName: "Driver Name",
        field: "driver_name",
        minWidth: 160,
      },
      {
        headerName: "Driver Phone",
        field: "driver_phone",
        minWidth: 140,
        valueFormatter: ({ value }) => value || "N/A",
      },
      {
        headerName: "Carrier",
        field: "carrier",
        minWidth: 120,
      },
      {
        headerName: "Check Point",
        field: "ivms_point",
        minWidth: 140,
        valueFormatter: ({ value }) => value || "N/A",
      },
      {
        headerName: "Issue",
        field: "issue",
        minWidth: 130,
      },
      {
        headerName: "Remarks",
        field: "remarks",
        minWidth: 220,
        flex: 1.4,
        cellClass: "bt-remarks-cell",
        valueFormatter: ({ value }) => value || "N/A",
      },
      {
        headerName: "Truck Status",
        field: "truck_status",
        minWidth: 140,
      },
      {
        headerName: "Breakdown Status",
        field: "breakdow_status",
        minWidth: 160,
        cellRenderer: ({ value }) => (
          <div className="bt-status-badge">{value || "Pending"}</div>
        ),
      },
      {
        headerName: "JP Status",
        field: "jp_status",
        minWidth: 140,
        cellRenderer: ({ value }) => (
          <div className={`bt-jp-status ${value?.toLowerCase()}`}>
            {value?.replace(/_/g, " ") || "N/A"}
          </div>
        ),
      },
      {
        headerName: "Date Resolved",
        field: "date_resolved",
        minWidth: 170,
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        valueFormatter: ({ value }) => formatDateTime(value),
      },
      {
        headerName: "Actions",
        field: "actions",
        minWidth: 130,
        filter: false,
        sortable: false,
        cellRenderer: ({ data: breakdown }) => (
          <div className="bt-action-icons">
            <button
              className="bt-action-btn bt-edit-btn"
              title="Edit"
              onClick={() =>
                navigate(`/edit-breakdown/${breakdown.breakdown_id}`)
              }
            >
              <FaEdit />
            </button>
            <button
              className="bt-action-btn bt-handle-btn"
              title="Handle"
              onClick={() =>
                navigate(`/handle-breakdown/${breakdown.breakdown_id}`)
              }
            >
              <FaTools />
            </button>
            <button
              className="bt-action-btn bt-info-btn"
              title="Info"
              onClick={() =>
                navigate(`/info-breakdown/${breakdown.breakdown_id}`)
              }
            >
              <FaInfoCircle />
            </button>
          </div>
        ),
        pinned: "right",
      },
    ],
    [dateFilterParams, navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: false,
    }),
    []
  );

  const resetTableFilters = () => {
    setSearchInput("");
    setGlobalFilter("");

    const api = gridRef.current?.api;
    if (!api) return;

    api.setFilterModel(null);
    api.applyColumnState({
      defaultState: { sort: null },
    });
  };

  const getExportRows = () => {
    const visibleRows = [];
    const api = gridRef.current?.api;

    if (!api) return filteredBreakdowns;

    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) visibleRows.push(node.data);
    });

    return visibleRows;
  };

  const handleExport = () => {
    const rowsToExport = getExportRows();

    if (!rowsToExport.length) {
      window.alert("No breakdown records match the current filters.");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to export ${rowsToExport.length} records to Excel?`
      )
    ) {
      const exportData = rowsToExport.map((breakdown) => ({
        "JP Number": breakdown.journey_plane_no,
        "Truck Number": breakdown.scheduled_vehicle,
        "Breakdown Date": formatDateTime(breakdown.breakdown_time),
        "Driver Name": breakdown.driver_name,
        "Driver Phone": breakdown.driver_phone || "N/A",
        Carrier: breakdown.carrier,
        "Last Check Point": breakdown.ivms_point || "N/A",
        Issue: breakdown.issue,
        Remarks: breakdown.remarks || "N/A",
        "Truck Status": breakdown.truck_status,
        "Breakdown Status": breakdown.breakdow_status,
        "JP Status": breakdown.jp_status?.replace(/_/g, " ") || "N/A",
        "Date Resolved": formatDateTime(breakdown.date_resolved),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      if (exportData.length) {
        const headers = Object.keys(exportData[0]);
        ws["!autofilter"] = {
          ref: XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: exportData.length, c: headers.length - 1 },
          }),
        };
        ws["!cols"] = headers.map((header) => ({
          wch: Math.min(
            45,
            Math.max(
              header.length,
              ...exportData.map((row) =>
                row[header] ? String(row[header]).length : 0
              )
            ) + 2
          ),
        }));
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Breakdowns");

      const status = selectedStatus || "ALL";
      const date = new Date().toISOString().split("T")[0];
      const fileName = `Breakdowns_${status}_${date}.xlsx`;

      XLSX.writeFile(wb, fileName);
    }
  };

  // Color palettes for charts
  const PIE_COLORS = [
    "#4f46e5",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
  ];
  const BAR_COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

  const hasData = filteredBreakdowns.length > 0;

  return (
    <div className="bt-container">
      {/* Header */}
      {/* Header */}
      <div className="bt-header">
        <div className="bt-header-main">
          <div className="bt-header-left">
            {/* This is the back arrow button */}
            <button
              className="bt-back-btn"
              onClick={() => navigate("/main")}
              title="Back to Main Page"
            >
              <FaArrowLeft />
            </button>
            <div className="bt-header-title">
              <h1>Breakdown Tracker</h1>
              <p>Manage and analyze vehicle breakdown records</p>
            </div>
          </div>
          <div className="bt-header-actions">
            <div className="bt-filter-group">
              <FaFilter className="bt-filter-icon" />
              <select
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="bt-status-filter"
              >
                <option value="">All Status</option>
                <option value="IN_TRANSIT">IN TRANSIT</option>
                <option value="CLOSED">CLOSED</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bt-main-content">
        {/* Action Bar */}
        <div className="bt-action-bar">
          <div className="bt-action-left">
            <button
              onClick={() => navigate("/add-breakdown")}
              className="bt-primary-btn"
            >
              <FaPlus />
              Add New Breakdown
            </button>
          </div>
          <div className="bt-action-right">
            <div className="bt-search-box">
              <FaSearch className="bt-search-icon" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="bt-search-input"
              />
            </div>
            <button
              className="bt-icon-btn"
              onClick={resetTableFilters}
              title="Reset table filters"
              aria-label="Reset table filters"
              type="button"
            >
              <FaSyncAlt />
            </button>
            <button
              className="bt-export-btn"
              onClick={handleExport}
              disabled={!hasData}
            >
              <FaFileDownload />
              Export Excel
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bt-tab-nav">
          <button
            className={`bt-tab-btn ${
              activeTab === "table" ? "bt-tab-active" : ""
            }`}
            onClick={() => setActiveTab("table")}
          >
            <FaTable />
            Breakdown List
            <span className="bt-tab-count">{filteredBreakdowns.length}</span>
          </button>
          <button
            className={`bt-tab-btn ${
              activeTab === "dashboard" ? "bt-tab-active" : ""
            }`}
            onClick={() => setActiveTab("dashboard")}
            disabled={!hasData}
          >
            <FaChartPie />
            Analytics Dashboard
          </button>
        </div>

        {/* Table View */}
        {activeTab === "table" && (
          <div className="bt-table-section">
            <div className="bt-table-container">
              {hasData ? (
                <div className="ag-theme-alpine bt-ag-grid">
                  <AgGridReact
                    ref={gridRef}
                    rowData={filteredBreakdowns}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowHeight={28}
                    headerHeight={36}
                    pagination={true}
                    paginationPageSize={40}
                    animateRows={true}
                  />
                </div>
              ) : (
                <div className="bt-empty-state">
                  <FaTachometerAlt className="bt-empty-icon" />
                  <h3>No breakdown records found</h3>
                  <p>
                    Try adjusting your search or filter criteria, or add new
                    breakdown records
                  </p>
                  <button
                    onClick={() => navigate("/add-breakdown")}
                    className="bt-primary-btn"
                  >
                    <FaPlus />
                    Add First Breakdown
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dashboard View */}
        {activeTab === "dashboard" && (
          <div className="bt-dashboard-section">
            {hasData ? (
              <>
                {/* Stats Overview */}
                <div className="bt-stats-overview">
                  <div className="bt-stat-card">
                    <div className="bt-stat-icon total">
                      <FaTachometerAlt />
                    </div>
                    <div className="bt-stat-info">
                      <h3>Total Breakdowns</h3>
                      <span className="bt-stat-value">
                        {filteredBreakdowns.length}
                      </span>
                    </div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon in-transit">
                      <FaTools />
                    </div>
                    <div className="bt-stat-info">
                      <h3>In Transit</h3>
                      <span className="bt-stat-value">
                        {
                          filteredBreakdowns.filter(
                            (b) => b.jp_status === "IN_TRANSIT"
                          ).length
                        }
                      </span>
                    </div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon resolved">
                      <FaInfoCircle />
                    </div>
                    <div className="bt-stat-info">
                      <h3>Resolved</h3>
                      <span className="bt-stat-value">
                        {
                          filteredBreakdowns.filter((b) => b.resolved === "Y")
                            .length
                        }
                      </span>
                    </div>
                  </div>

                  <div className="bt-stat-card">
                    <div className="bt-stat-icon pending">
                      <FaChartPie />
                    </div>
                    <div className="bt-stat-info">
                      <h3>Pending</h3>
                      <span className="bt-stat-value">
                        {
                          filteredBreakdowns.filter((b) => b.resolved !== "Y")
                            .length
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Charts */}
                <div className="bt-charts-grid">
                  <div className="bt-chart-card">
                    <div className="bt-chart-header">
                      <h3>Breakdown Type Distribution</h3>
                    </div>
                    <div className="bt-chart-container">
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie
                            data={chartData.pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={true}
                            label={({ name, percentage }) =>
                              `${name}: ${percentage}%`
                            }
                            outerRadius={100}
                            innerRadius={60}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {chartData.pieData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={PIE_COLORS[index % PIE_COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value, name, props) => [
                              `${value} (${props.payload.percentage}%)`,
                              name,
                            ]}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="bt-chart-card">
                    <div className="bt-chart-header">
                      <h3>Breakdowns by Carrier</h3>
                    </div>
                    <div className="bt-chart-container">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={chartData.barData}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f3f4f6"
                          />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar
                            dataKey="count"
                            name="Number of Breakdowns"
                            fill={BAR_COLORS[0]}
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bt-empty-dashboard">
                <FaChartPie className="bt-empty-icon" />
                <h3>No data available for analysis</h3>
                <p>Add breakdown records to view analytics dashboard</p>
                <button
                  onClick={() => navigate("/add-breakdown")}
                  className="bt-primary-btn"
                >
                  <FaPlus />
                  Add Breakdown Record
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BreakdownTracker;
